import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "@emotion/styled";
import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getVerticalCoverImage } from "../../utils/cms.utils";
import OptimizedImage from "../common/OptimizedImage";
import ContentTag from "../../utils/tag";
import { getAgeRangePublicRoute } from "../../utils/ageRange.util";
import { FOCUS_BORDER } from "../../config/focusStyles";
import { useInputMode } from "../../utils/hooks/useInputMode";

var ContentRowScrollingWrapper = styled.div`
  -webkit-flex-shrink: 1;
  flex-shrink: 1;
  -webkit-flex-grow: 1;
  flex-grow: 1;
  padding-left: 60px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

var ContentRowScrollingContent = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: row;
  flex-direction: row;
  -webkit-flex-wrap: nowrap;
  flex-wrap: nowrap;
  margin-right: 5%;
  
`;

var ContentRowWrapper = styled.div`
  padding-bottom: 37px;
  position: relative;
  min-height: 400px;
  padding-top: 40px;
  background-color: ${({ kind }) =>
    kind === "music" ? "#121212" : "transparent"};
  border-radius: ${({ kind }) => (kind === "music" ? "20px" : "0")};
  -webkit-border-radius: ${({ kind }) => (kind === "music" ? "20px" : "0")};
  width: ${({ kind }) => (kind === "music" ? "calc(100% - 5rem)" : "100%")};
  margin: ${({ kind }) => (kind === "music" ? "0 auto" : "0")};

  &:after {
    content: "";
    position: absolute;
    width: 150px;
    height: 100%;
    background: ${({ kind }) =>
    kind !== "music"
      ? "-webkit-linear-gradient(left, #121212, rgba(0, 0, 0, 0))"
      : "none"};
    background: ${({ kind }) =>
    kind !== "music"
      ? "linear-gradient(to right, #121212, rgba(0, 0, 0, 0))"
      : "none"};
    left: 0;
    top: 0;
    pointer-events: none;
  }
`;

var MovieCardWrapper = styled.div`
  position: relative;
  padding-top: 20px;
  margin-left: 8px;
  padding-left: ${({ $isLast }) => ($isLast ? "5%" : "0")};
`;

var MovieCardContainer = styled.div`
  width: 270px;
  min-width: 215px;
  min-height: 350px;
  border-radius: 15px;
  -webkit-border-radius: 15px;
  background-size: cover;
  background-position: center;
  overflow: hidden;
  margin-bottom: 5%;
  position: relative;
  z-index: ${(props) => (props.focused ? 10 : 0)};
  margin: 2px;
  border: ${(props) => (props.focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive)};
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  -webkit-transition: all 0.15s ease;
  transition: all 0.15s ease;
`;

var MovieOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${(props) => (props.visible ? 1 : 0)};
  pointer-events: none;
  -webkit-transition: opacity 0.2s;
  transition: opacity 0.2s;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  justify-content: center;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  padding-left: 16px;
  padding-right: 16px;
  -webkit-flex-direction: column;
  flex-direction: column;
  gap: 1rem;
  z-index: 10;
`;

var MovieTitle = styled.span`
  color: white;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  font-family: 'Anjoman Max FN', sans-serif;
`;

var MovieYear = styled.span`
  color: white;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
  font-family: 'Anjoman Max FN', sans-serif;
`;

var SeriesPseudoWrapper = styled.div`
  position: absolute;
  bottom: 0;
  top: -1rem;
  right: 5px;
  width: 270px;
  height: 100%;
`;

var SeriesPseudoImage = styled.img`
  width: 100%;
  margin-top: 1rem;
`;

var MovieItem = function MovieItem(props) {
  var item = props.item;
  var scrollContainerRef = props.scrollContainerRef;

  var navigate = useNavigate();
  var contentPhotos = item.contentPhotos;
  var slug = item.slug;
  var tags = item.tags;
  var kind = item.kind;
  var contentType = item.contentType;

  var focusResult = useFocusable({
    onEnterPress: function () {
      navigate("/" + (contentType?.kind || kind) + "/" + slug);
    },
    onFocus: props.onFocus,
  });
  var focused = focusResult.focused;
  var ref = focusResult.ref;
  var focusSelf = focusResult.focusSelf;

  // Input mode tracking for scroll behavior
  var inputMode = useInputMode();
  var isMouseFocus = inputMode.isMouseFocus;
  var setMouseFocus = inputMode.setMouseFocus;

  // Scroll into view when focused using native scrolling (skip for mouse hover)
  useEffect(function () {
    if (focused && ref.current && scrollContainerRef?.current && !isMouseFocus) {
      var element = ref.current;
      var container = scrollContainerRef.current;

      // Try scrollIntoView with options first (modern browsers)
      try {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      } catch (e) {
        // Fallback for older browsers - manual scroll calculation
        var elementRect = element.getBoundingClientRect();
        var containerRect = container.getBoundingClientRect();
        var elementCenter = elementRect.left + (elementRect.width / 2);
        var containerCenter = containerRect.left + (containerRect.width / 2);
        var scrollOffset = elementCenter - containerCenter;
        container.scrollLeft = container.scrollLeft + scrollOffset;
      }
    }
  }, [focused, isMouseFocus]);

  // Get optimized image with LQIP and WebP support
  var imageData = getVerticalCoverImage(contentPhotos);

  var isSeries = contentType?.kind === "series" || kind === "series";

  var handleNavigate = function () {
    navigate("/" + (contentType?.kind || kind) + "/" + slug);
  };

  return (
    <MovieCardWrapper $isLast={props.isLast}>
      {isSeries && (
        <SeriesPseudoWrapper>
          <SeriesPseudoImage src="/serial_pseudo.svg" alt="" />
        </SeriesPseudoWrapper>
      )}
      <MovieCardContainer ref={ref} focused={focused} onMouseEnter={function () { setMouseFocus(true); focusSelf(); }}>
        <OptimizedImage
          src={imageData.src}
          className="cursor-pointer"
          style={{
            width: "100%",
            minHeight: "350px",
            objectFit: "cover",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: focused ? 10 : 0,
            borderRadius: "12px",
          }}
          onClick={handleNavigate}
        />
        {tags?.[0] && <ContentTag label={tags?.[0]} />}
        <MovieOverlay visible={focused}>
          <MovieTitle>{item.title}</MovieTitle>
          <MovieYear>{item.year}</MovieYear>
        </MovieOverlay>
      </MovieCardContainer>
    </MovieCardWrapper>
  );
};

export var MoviesSlider = function MoviesSlider(props) {
  const [seletedIndex, setSelectedIndex] = useState(0);
  var movies = props.movies;
  var onFocus = props.onFocus;
  var shapeType = props.shapeType || "movie";
  var id = props.id;
  var index = props.index;

  var ageRange = getAgeRangePublicRoute();
  var _items = movies.filter(function (x) {
    return (
      x.content?.ageRange <= ageRange ||
      x.contentMeta?.content?.ageRange <= ageRange ||
      x.ageRange <= ageRange
    );
  });

  var focusResult = useFocusable({
    onFocus: onFocus,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
  });
  var ref = focusResult.ref;
  var focusKey = focusResult.focusKey;

  var scrollingRef = useRef(null);

  if (_items.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentRowWrapper ref={ref} kind={shapeType} tabIndex={seletedIndex}>
        <ContentRowScrollingWrapper ref={scrollingRef}>
          <ContentRowScrollingContent>
            {_items.map(function (item, idx) {
              return (
                <MovieItem
                  item={item}
                  key={"ms_" + (item?.slug || idx) + "_" + id + "_" + idx}
                  scrollContainerRef={scrollingRef}
                  shapeType={shapeType}
                  isLast={idx === _items.length - 1}
                />
              );
            })}
          </ContentRowScrollingContent>
        </ContentRowScrollingWrapper>
      </ContentRowWrapper>
    </FocusContext.Provider>
  );
};
